/**
 * Load More Pagination - Alternative to infinite scroll
 * Provides better browser back/forward navigation support
 */

// Single storage key for all collections, so Cookiebot only ever sees one
// entry to classify instead of one per collection page visited.
const STORAGE_KEY = 'collection-state';

function readStoredState() {
  try {
    return JSON.parse(sessionStorage.getItem(STORAGE_KEY)) || {};
  } catch (error) {
    return {};
  }
}

function writeStoredState(state) {
  try {
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch (error) {
    console.error('Error saving collection state:', error);
  }
}

class LoadMorePagination {
  constructor() {
    /**
     * Infinite Scroll Mode
     * If true, hide the loading button and automatically load more when in view
     * If false, show a "Load More" button that must be clicked
     */
    this.IS_INFINITE_SCROLL = true;

    this.container = document.querySelector('.js-ajaxinate-grid');
    this.paginationWrapper = document.querySelector('.js-ajaxinate-pagination');
    this.loadMoreButton = null;
    this.isLoading = false;
    this.path = window.location.pathname;
    this.loadedPages = 1;
    this.originalText = '';

    if (this.container && this.paginationWrapper) {
      this.init();
    }
  }

  async init() {
    // Button before restore (replay updates it); observer after (would race it).
    this.createLoadMoreButton();
    await this.restoreState();
    this.bindEvents();
    this.trackProductClicks();

    // Essential event listeners for scroll restoration
    window.addEventListener('beforeunload', () => this.saveState());
    window.addEventListener('pageshow', (event) => {
      if (event.persisted) {
        setTimeout(() => this.restoreScrollPosition(), 100);
      }
    });
  }

  restoreScrollPosition() {
    const state = readStoredState()[this.path];
    if (state && state.scrollPosition) {
      window.scrollTo(0, state.scrollPosition);
    }
  }

  async restoreState() {
    const state = readStoredState()[this.path];
    if (!state) return;

    try {
      const targetPages = state.loadedPages || 1;

      // Replay the loads; saved innerHTML is dead markup (components gut it
      // post-init) and stale on prices/availability.
      if (targetPages > 1) {
        this.loadedPages = 1;
        for (let page = 1; page < targetPages; page++) {
          const loaded = await this.handleLoadMore();
          if (!loaded) break;
        }
      }

      if (state.scrollPosition) {
        setTimeout(() => {
          window.scrollTo(0, state.scrollPosition);
        }, 100);
      }
    } catch (error) {
      console.error('Error restoring collection state:', error);
      this.clearState();
    }
  }

  saveState() {
    const allState = readStoredState();
    allState[this.path] = {
      loadedPages: this.loadedPages,
      scrollPosition: window.pageYOffset || document.documentElement.scrollTop,
      timestamp: Date.now(),
    };
    writeStoredState(allState);
  }

  clearState() {
    const allState = readStoredState();
    delete allState[this.path];
    writeStoredState(allState);
  }

  trackProductClicks() {
    // Save state before navigating to product pages
    this.container.addEventListener('click', (event) => {
      const productLink = event.target.closest('a[href*="/products/"]');
      if (productLink) {
        this.saveState();
      }
    });
  }

  createLoadMoreButton() {
    const nextLink = this.paginationWrapper.querySelector('.js-ajaxinate-pagination-next-link');
    this.loadMoreButton = document.querySelector('.js-load-more-button');

    if (!nextLink && this.loadedPages === 1) {
      // No pagination needed, hide the button
      if (this.loadMoreButton) {
        this.loadMoreButton.style.display = 'none';
      }
      return;
    }

    // Hide original pagination
    this.paginationWrapper.style.display = 'none';

    // Show and configure the load more button if we have more pages to load
    if (nextLink && this.loadMoreButton) {
      this.loadMoreButton.style.display = 'block';
      this.loadMoreButton.disabled = false;

      // Get translatable text from data attributes
      this.originalText = this.loadMoreButton.textContent.trim();
      this.loadingText = this.loadMoreButton.dataset.loadingText || 'Loading...';
      this.errorText = this.loadMoreButton.dataset.errorText || 'Error - Try Again';

      if (this.IS_INFINITE_SCROLL) {
        // Hide button text for infinite scroll mode
        this.loadMoreButton.style.visibility = 'hidden';
      }
    } else if (this.loadMoreButton) {
      // No more pages, hide the button
      this.loadMoreButton.style.display = 'none';
    }
  }

  bindEvents() {
    if (!this.loadMoreButton) return;

    if (this.IS_INFINITE_SCROLL) {
      // Trigger a click on the load more button when the button is in view
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              this.handleLoadMore();
            }
          });
        },
        {
          rootMargin: '0px 0px 200px 0px', // Trigger a bit before the button is fully in view
        }
      );

      observer.observe(this.loadMoreButton);
    } else {
      this.loadMoreButton.addEventListener('click', this.handleLoadMore.bind(this));
    }
  }

  async handleLoadMore() {
    if (this.isLoading) {
      return false;
    }

    const nextLink = this.paginationWrapper.querySelector('.js-ajaxinate-pagination-next-link');
    if (!nextLink) return false;

    this.isLoading = true;
    this.loadMoreButton.textContent = this.loadingText;
    this.loadMoreButton.disabled = true;

    try {
      // Ensure products are loaded in numbers divisible by 3 for proper grid layout
      let fetchUrl = nextLink.href;
      const url = new URL(fetchUrl);
      const currentPerPage = parseInt(url.searchParams.get('per_page')) || 24;

      // Round to nearest number divisible by 3
      let adjustedPerPage = currentPerPage;
      if (adjustedPerPage % 3 !== 0) {
        adjustedPerPage = Math.ceil(adjustedPerPage / 3) * 3;
      }

      url.searchParams.set('per_page', adjustedPerPage.toString());
      fetchUrl = url.toString();

      const response = await fetch(fetchUrl);

      if (!response.ok) {
        throw new Error(`Network error: ${response.status}`);
      }

      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'text/html');

      // Get new products
      const newContainer = doc.querySelector('.js-ajaxinate-grid');
      if (newContainer && newContainer.children.length > 0) {
        this.container.insertAdjacentHTML('beforeend', newContainer.innerHTML);
        this.loadedPages++;
      } else {
        throw new Error('No products found in response');
      }

      // Update pagination
      const newPagination = doc.querySelector('.js-ajaxinate-pagination');
      if (newPagination) {
        this.paginationWrapper.innerHTML = newPagination.innerHTML;
        const newNextLink = this.paginationWrapper.querySelector('.js-ajaxinate-pagination-next-link');

        if (newNextLink) {
          this.loadMoreButton.textContent = this.originalText;
          this.loadMoreButton.disabled = false;
        } else {
          // No more pages
          this.loadMoreButton.style.display = 'none';
        }
      } else {
        // No more pages
        this.loadMoreButton.style.display = 'none';
      }

      // Trigger grid formatting
      if (typeof formatGridItems === 'function') {
        formatGridItems();
      }

      // Save the current state after loading more
      this.saveState();
      this.isLoading = false;
      return true;
    } catch (error) {
      console.error('Error loading more products:', error);
      this.loadMoreButton.textContent = this.errorText;
      this.loadMoreButton.disabled = false;
    }

    this.isLoading = false;
    return false;
  }

  // Static method to clean up any existing instance
  static cleanup() {
    if (window.loadMorePaginationInstance) {
      window.loadMorePaginationInstance = null;
    }
  }
}

// Handle page visibility changes to save state
document.addEventListener('visibilitychange', () => {
  if (document.visibilityState === 'hidden') {
    const instance = window.loadMorePaginationInstance;
    if (instance) {
      instance.saveState();
    }
  }
});

// Clear state when filters are applied (facets change)
document.addEventListener('facets:updated', () => {
  sessionStorage.removeItem(STORAGE_KEY);

  // Reinitialize the Load More pagination for the new filtered content
  setTimeout(() => {
    if (document.querySelector('.js-ajaxinate-grid') && document.querySelector('.js-ajaxinate-pagination')) {
      LoadMorePagination.cleanup();
      const instance = new LoadMorePagination();
      window.loadMorePaginationInstance = instance;
    }
  }, 100);
});

// Initialize when DOM is ready
window.addEventListener('load', () => {
  // Only initialize if we're on a collection page with infinite scroll elements
  if (document.querySelector('.js-ajaxinate-grid') && document.querySelector('.js-ajaxinate-pagination')) {
    const instance = new LoadMorePagination();
    window.loadMorePaginationInstance = instance;
  }
});
